import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { QRCodeCanvas } from "qrcode.react";
import { QrCode, RefreshCw, Copy, Printer, Download } from "lucide-react";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

/**
 * QR code check-in dialog for a rehearsal.
 * - Fetches / lazily creates a check-in token
 * - Renders a QR encoding `${origin}/checkin/{rehearsalId}?t={token}`
 * - Rotate/copy/print/download actions for the stage manager
 */
export default function RehearsalCheckinQR({ rehearsalId, rehearsalTitle }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);

  const url = token
    ? `${window.location.origin}/checkin/${rehearsalId}?t=${token}`
    : "";

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/rehearsals/${rehearsalId}/checkin-token`);
      setToken(data.token);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load check-in code");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open && !token) load();
  }, [open, token]);

  const rotate = async () => {
    if (!confirm("Rotate the check-in code? Any printed QR codes for this rehearsal will stop working.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/rehearsals/${rehearsalId}/checkin-token/rotate`);
      setToken(data.token);
      toast.success("New check-in code generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rotate failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    if (!url) return;
    navigator.clipboard?.writeText(url);
    toast.success("Check-in link copied");
  };

  const download = () => {
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `stagecall-checkin-${rehearsalTitle || rehearsalId}.png`.replace(/[^a-z0-9._-]/gi, "_");
    link.href = canvas.toDataURL("image/png");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const printQr = () => {
    if (!url) return;
    const w = window.open("", "_blank", "width=600,height=800");
    if (!w) return;
    const canvas = canvasRef.current?.querySelector("canvas");
    const dataUrl = canvas ? canvas.toDataURL("image/png") : "";
    w.document.write(`<!DOCTYPE html><html><head><title>Check-in · ${rehearsalTitle || ""}</title>
      <style>
        body{font-family:-apple-system,BlinkMacSystemFont,sans-serif;background:#fff;color:#0A0A0A;padding:40px;text-align:center;}
        h1{font-family:Georgia,serif;font-size:32px;margin:0 0 8px 0;}
        h2{font-size:14px;letter-spacing:.3em;text-transform:uppercase;color:#71717a;margin:0 0 32px 0;}
        img{max-width:420px;width:100%;height:auto;}
        p{font-size:14px;color:#52525b;margin-top:32px;}
        code{background:#f4f4f5;padding:6px 10px;border-radius:4px;font-size:12px;}
        @media print { body{padding:20px;} }
      </style>
      </head><body>
      <h2>Scan to check in</h2>
      <h1>${(rehearsalTitle || "Rehearsal").replace(/</g, "&lt;")}</h1>
      <img src="${dataUrl}" alt="QR code" />
      <p>Or open the link on any phone:<br/><code>${url.replace(/</g, "&lt;")}</code></p>
      <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <>
      <button
        data-testid="open-checkin-qr-btn"
        onClick={() => setOpen(true)}
        className="h-8 px-2.5 rounded border border-gold/40 text-gold hover:bg-gold/10 text-xs inline-flex items-center gap-1.5"
        title="Show check-in QR"
      >
        <QrCode className="w-3.5 h-3.5" /> Check-in QR
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Rehearsal check-in</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-xs text-zinc-400 leading-relaxed">
              Cast &amp; staff can scan this at the door to mark themselves present. Have them scan
              again at the end to record check-out. They need to be signed in on their phone.
            </p>

            <div className="rounded-lg border border-white/10 bg-white p-6 grid place-items-center" ref={canvasRef}>
              {token ? (
                <QRCodeCanvas value={url} size={220} includeMargin={false} level="M" data-testid="checkin-qr-code" />
              ) : (
                <div className="w-[220px] h-[220px] grid place-items-center text-zinc-400 text-xs">
                  {busy ? "Loading…" : "No code yet"}
                </div>
              )}
            </div>

            {url && (
              <div className="rounded-md border border-white/10 bg-[#121212] p-3">
                <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Check-in link</div>
                <div className="mt-1 text-[11px] font-mono text-zinc-300 break-all">{url}</div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="copy-checkin-link"
                onClick={copy}
                disabled={!url}
                className="h-9 rounded border border-white/10 text-sm text-zinc-200 hover:bg-white/5 inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
              >
                <Copy className="w-4 h-4" /> Copy link
              </button>
              <button
                data-testid="download-checkin-qr"
                onClick={download}
                disabled={!token}
                className="h-9 rounded border border-white/10 text-sm text-zinc-200 hover:bg-white/5 inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
              >
                <Download className="w-4 h-4" /> Download PNG
              </button>
              <button
                data-testid="print-checkin-qr"
                onClick={printQr}
                disabled={!token}
                className="h-9 rounded border border-white/10 text-sm text-zinc-200 hover:bg-white/5 inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
              >
                <Printer className="w-4 h-4" /> Print
              </button>
              <button
                data-testid="rotate-checkin-token"
                onClick={rotate}
                disabled={busy}
                className="h-9 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
              >
                <RefreshCw className="w-4 h-4" /> Rotate code
              </button>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setOpen(false)}
              style={goldBtn}
              className="h-9 px-4 rounded font-semibold text-sm"
            >
              Done
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
